/**
 * Reader review of a device panel (web-demo-backend `/api/device-panels/.../review`):
 * a queue of device findings to check, and each reviewer's verdicts on them, in
 * rounds. In the viewer's rail, the blind read judges the images alone and the
 * report check re-judges a finding with the report's sentence about it shown. On
 * a notification page, the notify round judges whether a finding should notify a
 * radiologist (it can be present and still not worth the interruption), the
 * key-image round judges whether a finding's key image shows it, and the missed round judges findings a device claims but did not
 * flag. Each round's verdicts are kept apart. Items and verdicts are keyed by
 * (device, condition) within a study, and verdicts also by round.
 */

import { panelCaseUrl } from "@/types/devicePanel";

/** The rounds judged in the viewer's rail. */
export type RailRound = "blind" | "report_check";
export type ReviewRound = RailRound | "notify" | "key_image" | "missed";

/** The rail's rounds in order, with their names. Blind is the default. */
const ROUNDS: readonly { value: RailRound; label: string }[] = [
  { value: "blind", label: "Blind read" },
  { value: "report_check", label: "Report check" },
];

/** A round's name, e.g. "Report check". */
export const roundLabel = (round: RailRound) =>
  ROUNDS.find((entry) => entry.value === round)!.label;

export type BlindKind =
  | "finding_check"
  | "possible_unreported_finding"
  | "triage_overcall"
  | "triage_overcall_judges_disagree"
  | "triage_below_default_in_report";

export type ReviewKind = BlindKind | "report_check" | "possible_missed_finding";

/** The tag a queued finding wears on its row in its round. */
export const REVIEW_TAGS: Record<ReviewKind, string> = {
  finding_check: "Check",
  possible_unreported_finding: "Not in report",
  triage_overcall: "Not in report",
  triage_overcall_judges_disagree: "Not in report",
  triage_below_default_in_report: "Below default",
  report_check: "Report check",
  possible_missed_finding: "Possible miss",
};

const KIND_ROUNDS: Record<ReviewKind, ReviewRound> = {
  finding_check: "blind",
  possible_unreported_finding: "blind",
  triage_overcall: "blind",
  triage_overcall_judges_disagree: "blind",
  triage_below_default_in_report: "blind",
  report_check: "report_check",
  possible_missed_finding: "missed",
};

/** The round a queue item belongs to. */
export const roundOf = (kind: ReviewKind): ReviewRound => KIND_ROUNDS[kind];

/** What each round's verdict buttons answer. */
export const ROUND_PROMPTS: Record<ReviewRound, string> = {
  blind: "On images",
  report_check: "Report check",
  notify: "Notify",
  key_image: "Key image",
  missed: "On images",
};

export type BlindVerdict = "present" | "absent" | "uncertain";
export type ReportCheckVerdict = "keep" | "revise" | "uncertain";
export type NotifyVerdict = "yes" | "no";
export type KeyImageVerdict = "shows_finding" | "wrong_slice" | "uncertain";
export type VerdictValue = BlindVerdict | ReportCheckVerdict | NotifyVerdict | KeyImageVerdict;

const IMAGE_TRUTH = [
  { value: "present", label: "Present", key: "1" },
  { value: "absent", label: "Absent", key: "2" },
  { value: "uncertain", label: "Unsure", key: "3" },
] as const;

/** Each round's verdict buttons in order, with their labels and keyboard keys. */
export const VERDICTS: Record<
  ReviewRound,
  readonly { value: VerdictValue; label: string; key: string }[]
> = {
  blind: IMAGE_TRUTH,
  report_check: [
    { value: "keep", label: "Keep", key: "1" },
    { value: "revise", label: "Revise", key: "2" },
    { value: "uncertain", label: "Unsure", key: "3" },
  ],
  notify: [
    { value: "yes", label: "Yes", key: "1" },
    { value: "no", label: "No", key: "2" },
  ],
  key_image: [
    { value: "shows_finding", label: "Shows it", key: "1" },
    { value: "wrong_slice", label: "Wrong slice", key: "2" },
    { value: "uncertain", label: "Unsure", key: "3" },
  ],
  missed: IMAGE_TRUTH,
};

/** A verdict's button label in its round. */
export function verdictLabel(round: ReviewRound, value: VerdictValue): string {
  const verdict = VERDICTS[round].find((candidate) => candidate.value === value);
  if (!verdict) throw new Error(`${value} is not a ${round} verdict`);
  return verdict.label;
}

/** The longest note the backend accepts. */
export const NOTE_MAX_LENGTH = 1000;

export interface ReviewTarget {
  device: string;
  condition: string;
}

/** A queued finding. A report-check item carries the report's sentence about it. */
export type ReviewItem = ReviewTarget &
  (
    | { kind: BlindKind | "possible_missed_finding" }
    | { kind: "report_check"; report_phrase: string }
  );

export interface ReviewVerdict extends ReviewTarget {
  round: ReviewRound;
  reviewer: string;
  verdict: VerdictValue;
  note: string | null;
  updated_at: string;
}

/** A finding a device claims but did not flag at its default, by name only. */
export interface MissableFinding extends ReviewTarget {
  display_name: string;
}

export interface StudyReviewResponse {
  reviewer: string;
  /** Whether the caller is one of the panel's reviewers. */
  can_review: boolean;
  items: ReviewItem[];
  /** The caller's own verdicts, every round. */
  verdicts: ReviewVerdict[];
  /** Every other reviewer's, for internal callers; null otherwise. */
  other_verdicts: ReviewVerdict[] | null;
  /** What the missed round may judge; empty for a caller who cannot review. */
  missable: MissableFinding[];
}

/** One round's queue: its items and the caller's verdicts in that round. */
export interface ReviewProgressResponse {
  reviewer: string;
  round: ReviewRound;
  /** Every queued study, in the order the backend sends. */
  studies: {
    study_uid: string;
    label: string;
    items: number;
    reviewed_items: number;
    verdicts: number;
  }[];
  totals: { items: number; reviewed_items: number; verdicts: number };
}

export const sameTarget = (a: ReviewTarget, b: ReviewTarget) =>
  a.device === b.device && a.condition === b.condition;

/** Whether two verdicts are the same reviewer slot: one finding in one round. */
export const sameVerdictSlot = (
  a: ReviewTarget & { round: ReviewRound },
  b: ReviewTarget & { round: ReviewRound },
) => sameTarget(a, b) && a.round === b.round;

/** The `?finding=` value naming one device's finding, e.g. "abdo:free_air". */
export const findingParam = ({ device, condition }: ReviewTarget) => `${device}:${condition}`;

/** The finding a `?finding=` value names (split at the first colon); null when
 * it names none. */
export function parseFindingParam(value: string): ReviewTarget | null {
  const at = value.indexOf(":");
  return at > 0 && at < value.length - 1
    ? { device: value.slice(0, at), condition: value.slice(at + 1) }
    : null;
}

/** Where a study opens in the rail without a named finding: on its first
 * unjudged row, or on its last row (stepping back from the next study). */
export type RowPosition = "next" | "last";

/** A study's case page in Review for a round, opened at `at` in its rail. */
export function reviewUrl(
  panelId: string,
  studyUid: string,
  round: ReviewRound,
  at: RowPosition,
): string {
  return `${panelCaseUrl(panelId, studyUid)}?${new URLSearchParams({ mode: "review", round, at })}`;
}

/** The row position an `?at=` value names; null when unknown. */
export const parseAtParam = (value: string): RowPosition | null =>
  value === "next" || value === "last" ? value : null;

/** How a viewer sees the panel: View (the devices' output alone, the default
 * for everyone) or Review (the same rows with the caller's review task on them). */
export type PanelMode = "view" | "review";

/** The mode a `?mode=` value names; null when unknown. */
export const parseModeParam = (value: string): PanelMode | null =>
  value === "view" || value === "review" ? value : null;

/** The round a `?round=` value names; null when unknown. */
export const parseRoundParam = (value: string): RailRound | null =>
  ROUNDS.find((round) => round.value === value)?.value ?? null;

/** The caller's review task on a panel: the round it opens on and what is left
 * there, and the switch's label (the round's name when the caller has items in
 * more than one round). */
export interface ReviewTask {
  round: RailRound;
  left: number;
  label: string;
}

/**
 * The caller's task from each round's progress (absent while loading, or for a
 * caller the backend gives no review data): null when no round has items for
 * them. It opens on `requested` when named, else the first round with items
 * left, else the last round with items.
 */
export function reviewTask(
  progress: Partial<Record<RailRound, ReviewProgressResponse>>,
  requested: RailRound | null,
): ReviewTask | null {
  const withItems = ROUNDS.filter(({ value }) => (progress[value]?.totals.items ?? 0) > 0);
  if (!withItems.length) return null;
  const left = (round: RailRound) => {
    const totals = progress[round]?.totals;
    return totals ? totals.items - totals.reviewed_items : 0;
  };
  const round =
    requested ??
    withItems.find(({ value }) => left(value) > 0)?.value ??
    withItems[withItems.length - 1].value;
  return {
    round,
    left: left(round),
    label: withItems.length > 1 ? roundLabel(round) : "Review",
  };
}

/** Other reviewers' verdicts on a target in one round as a hint, e.g. "Others:
 * present ×1, absent ×2"; null when there are none (or they are not shown to this
 * caller). */
export function othersHint(
  others: readonly ReviewVerdict[] | null,
  target: ReviewTarget,
  round: ReviewRound,
): string | null {
  const counts = VERDICTS[round]
    .map(({ value }) => ({
      value,
      count: (others ?? []).filter(
        (verdict) => sameVerdictSlot(verdict, { ...target, round }) && verdict.verdict === value,
      ).length,
    }))
    .filter(({ count }) => count > 0);
  return counts.length
    ? `Others: ${counts.map(({ value, count }) => `${value} ×${count}`).join(", ")}`
    : null;
}

/** The study after `studyUid` in queue order (wrapping) that still has items to
 * review; null when no other study does. A study outside the queue starts it. */
export function nextToReview(
  studies: ReviewProgressResponse["studies"],
  studyUid: string,
): ReviewProgressResponse["studies"][number] | null {
  const at = studies.findIndex((study) => study.study_uid === studyUid);
  const after = [...studies.slice(at + 1), ...studies.slice(0, Math.max(at, 0))];
  return after.find((study) => study.reviewed_items < study.items) ?? null;
}

/** The study before `studyUid` in queue order; null at the first, or outside the queue. */
export function previousInQueue(
  studies: ReviewProgressResponse["studies"],
  studyUid: string,
): ReviewProgressResponse["studies"][number] | null {
  const at = studies.findIndex((study) => study.study_uid === studyUid);
  return at > 0 ? studies[at - 1] : null;
}

/** The first study in queue order with items still to review; null when none. */
export const firstToReview = (studies: ReviewProgressResponse["studies"]) =>
  studies.find((study) => study.reviewed_items < study.items) ?? null;
